import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { queryClient } from "@/api";
import { getGetAdminPermissionsQueryKey } from "@/api/orval/auth/auth";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/Button";
import { ReactComponent as FrownIcon } from "atoms/icons/frown.svg";

export type AuthorizationErrorViewProps = {
  "className"?: string;
  "data-testid"?: string;
};

export const AuthorizationErrorView = ({
  className = "flex min-h-screen w-full items-center justify-center bg-background",
  "data-testid": dataTestId,
}: AuthorizationErrorViewProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  /**
   * There's a chance that user's permissions changed right while
   * the user was navigating the app.
   * Invalidate `/auth/permissions` endpoint so that we refetch
   * them.
   */
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: getGetAdminPermissionsQueryKey() });
    queryClient.invalidateQueries({ queryKey: ["platformPermissions"] });
  }, []);

  return (
    <div className={className}>
      <div
        className="flex flex-col items-center justify-center px-2 text-center"
        data-testid={dataTestId}
      >
        <FrownIcon title={t("common.authorizationError.iconTitle")} />
        <p className="mt-4 text-bold1">{t("common.authorizationError.noPermission")}</p>
        <p className="mt-4 text-bold1">{t("common.authorizationError.requestAccess")}</p>
        <div className="mt-8 flex gap-7">
          <Button
            style="outline"
            /**
             * This intentionally leads to platform-overview
             * because (unlike `/` or `/admin/platforms/`) it correctly
             * handles multi-platform user case.
             */
            onClick={() => navigate("/admin/platform-overview/")}
          >
            {t("common.authorizationError.returnHome")}
          </Button>
        </div>
      </div>
    </div>
  );
};
